import React, { useState } from 'react';

const UserDeletionModal = ({ user, userDataCounts, onConfirm, onCancel }) => {
  const [deletionType, setDeletionType] = useState('');
  const [confirmUsername, setConfirmUsername] = useState('');

  const isAdmin = user.role === 'admin';
  const canHardDelete = userDataCounts.totalRecords < 10;

  const isConfirmEnabled = () => {
    if (!deletionType) return false;
    if (isAdmin && confirmUsername !== user.username) return false;
    return true;
  };

  const handleConfirm = () => {
    onConfirm(deletionType, confirmUsername);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Delete User Account</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-gray-600">@{user.username}</p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
              {isAdmin && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                  Admin
                </span>
              )}
            </div>
          </div>

          {/* Data Summary */}
          {userDataCounts.totalRecords > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">User Data Summary</h3>
              <div className="grid grid-cols-2 gap-3">
                {userDataCounts.checkins > 0 && (
                  <div className="bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-600">Check-ins:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {userDataCounts.checkins}
                    </span>
                  </div>
                )}
                {userDataCounts.sips > 0 && (
                  <div className="bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-600">Wine Ratings:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {userDataCounts.sips}
                    </span>
                  </div>
                )}
                {userDataCounts.lists > 0 && (
                  <div className="bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-600">Lists:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {userDataCounts.lists}
                    </span>
                  </div>
                )}
                {userDataCounts.friendships > 0 && (
                  <div className="bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-600">Friends:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {userDataCounts.friendships}
                    </span>
                  </div>
                )}
                {userDataCounts.referrals > 0 && (
                  <div className="bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-600">Referrals:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {userDataCounts.referrals}
                    </span>
                  </div>
                )}
                {userDataCounts.listingAccess > 0 && (
                  <div className="bg-gray-50 px-3 py-2 rounded">
                    <span className="text-sm text-gray-600">Listing Access:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {userDataCounts.listingAccess}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Total records: {userDataCounts.totalRecords}
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded text-center text-gray-600">
              No user data found
            </div>
          )}

          {/* Deletion Type Selection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Deletion Type</h3>
            <div className="space-y-3">
              {/* Soft Delete Option */}
              <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 transition">
                <input
                  type="radio"
                  name="deletionType"
                  value="soft"
                  checked={deletionType === 'soft'}
                  onChange={(e) => setDeletionType(e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-900">Soft Delete</span>
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                      Recommended
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Mark account as deleted. User data is preserved and the deletion can be
                    reversed. User cannot log in.
                  </p>
                </div>
              </label>

              {/* Hard Delete Option */}
              <label
                className={`flex items-start p-4 border-2 rounded-lg transition ${
                  canHardDelete
                    ? 'border-gray-200 cursor-pointer hover:border-gray-300'
                    : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                }`}
              >
                <input
                  type="radio"
                  name="deletionType"
                  value="hard"
                  checked={deletionType === 'hard'}
                  onChange={(e) => setDeletionType(e.target.value)}
                  disabled={!canHardDelete}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">Hard Delete</span>
                  <p className="text-sm text-gray-600 mt-1">
                    Permanently remove account and all data. This action cannot be undone.
                  </p>
                  {!canHardDelete && (
                    <p className="text-sm text-red-600 font-medium mt-2">
                      User has significant data ({userDataCounts.totalRecords} records). Hard
                      delete is only available for users with less than 10 records.
                    </p>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Hard Delete Warning */}
          {deletionType === 'hard' && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 font-semibold">
                    Warning: Permanent Data Loss
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    This will permanently delete the user account and all associated data. This
                    action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Admin Confirmation */}
          {isAdmin && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-yellow-700 font-semibold">
                    Admin Account Deletion Requires Confirmation
                  </p>
                  <p className="text-sm text-yellow-700 mt-1 mb-3">
                    Type the username <span className="font-mono font-bold">{user.username}</span>{' '}
                    to confirm deletion of this admin account.
                  </p>
                  <input
                    type="text"
                    value={confirmUsername}
                    onChange={(e) => setConfirmUsername(e.target.value)}
                    placeholder="Type username to confirm"
                    className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmEnabled()}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isConfirmEnabled()
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDeletionModal;
